import { useEffect, useRef, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { useDelayedMatch } from './useDelayedMatch';

/** Exported so tests can wait on the real delay rather than a hard-coded one. */
export const SEARCH_DEBOUNCE_MS = 400;

type QueryStatus = {
  isFetching: boolean;
  isPending: boolean;
};

type UseDebouncedUrlParamOptions = {
  /**
   * Gates commit of a non-empty debounced value. Invalid intermediates (e.g. a
   * partial production year) are not written, so the applied URL value stays
   * put until the input is valid or cleared.
   */
  isValid?: (value: string) => boolean;
  /** When set, {@link useDebouncedUrlParam} also returns `isSearchFetching`. */
  queryStatus?: QueryStatus;
};

/**
 * Spinner while search is in flight. Active on background refetch (not initial
 * load) or debounce lag. useDelayedMatch lingers after fetch so stale rows
 * don't flash. Backspacing skips the spinner when cached prefix results remain.
 */
export const useIsSearchFetching = (
  input: string,
  debounced: string,
  queryStatus: QueryStatus,
) =>
  useDelayedMatch(
    (queryStatus.isFetching && !queryStatus.isPending) ||
      debounced !== input.trim(),
    true,
  ) &&
  Boolean(input.trim()) &&
  !debounced.includes(input.trim());

/**
 * Local input state with a debounced commit to a URL-backed value. Resyncs the
 * input when `urlValue` changes externally (back/forward, sidebar clear).
 */
export const useDebouncedUrlParam = (
  urlValue: string,
  setUrlValue: (value: string) => void,
  options: UseDebouncedUrlParamOptions = {},
) => {
  const { isValid = () => true, queryStatus } = options;

  const [input, setInput] = useState(urlValue);
  const [debounced] = useDebounceValue(input.trim(), SEARCH_DEBOUNCE_MS);

  /** The last value this hook put in the URL, so it can recognise its own write. */
  const committedRef = useRef(urlValue);

  useEffect(() => {
    if (debounced !== '' && !isValid(debounced)) return;

    if (debounced !== urlValue) {
      committedRef.current = debounced;
      setUrlValue(debounced);
    }
    // Keyed on the debounced input alone: reacting to `urlValue` as well would
    // re-commit the current input to a URL that has just dropped the param.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  // `urlValue` becoming something this hook did not commit means the URL moved
  // on its own, and the field has to follow it.
  useEffect(() => {
    if (urlValue === committedRef.current) return;

    committedRef.current = urlValue;
    setInput(urlValue);
  }, [urlValue]);

  // Always call so hook order is stable when `queryStatus` is omitted.
  const isSearchFetching = useIsSearchFetching(
    input,
    debounced,
    queryStatus ?? { isFetching: false, isPending: false },
  );

  return {
    debounced,
    input,
    setInput,
    ...(queryStatus ? { isSearchFetching } : {}),
  };
};
